/**
 * Delete + cascade data layer (PTV2-1148, Phase 3).
 *
 * Every delete in the pedigree/family/patient graph is previewed first (a synchronous dry-run that
 * returns a truth-table of DELETE vs UNLINK counts) and then executed as a background job — the
 * execute endpoints return a `PhenotipsJobExecutionDTO` job handle, never a terminal result, and the
 * client polls `GET /jobs/{jobId}` until the Spring Batch status is terminal, then does a final
 * existence check before removing the card / navigating away (design doc 09 §8/§9, doc 10 Phase 3).
 *
 * Wire shapes are hand-typed (not yet in the generated OpenAPI schema): the v1 preview/delete DTOs are
 * snake_case (matching how `personPedigrees.ts` types this slice), while the core job DTO is camelCase
 * (core sets no Jackson naming strategy — same shape the platform console consumes).
 *
 * Routing: PATIENT/FAMILY go through the v1 `http` client; PEDIGREE (scenario d) targets `/api/v2/…`,
 * reached via the pedigree editor's auth-carrying `createPtv2Fetch` (the v1 `http` base is `/api/v1`).
 */
import { queryOptions } from '@tanstack/react-query';

import { http } from '@/api/http';

import { createPtv2Fetch } from '@/features/pedigree/ptv2Fetch';

/** The three delete targets. `PATIENT` is addressed via the `/persons/{guid}` endpoints. */
export type DeleteTargetType = 'PATIENT' | 'FAMILY' | 'PEDIGREE';

/** Family delete branch: f1 (`UNLINK` members, default) vs f2 (`DELETE` all members). */
export type MembersMode = 'UNLINK' | 'DELETE';

/**
 * A single person named in a delete preview — enough for the wizard to list *who* is affected.
 * `is_patient` is set for deleted persons (patient record vs pedigree-only individual) and left unset
 * for unlinked persons.
 */
export interface DeletePersonRef {
  readonly guid: string;
  readonly name: string;
  readonly is_patient?: boolean;
}

/**
 * `DeleteImpactDTO` — the dry-run cascade plan. Snake_case on the wire. Counts are the truth the UI
 * renders (never an estimate); `blocked` gates the whole action (v1 fork stop-sign, design §6/§7);
 * `requires_confirmation` switches the simple confirm for the type-to-confirm wizard.
 */
export interface DeleteImpactDto {
  readonly target_type: DeleteTargetType;
  readonly target_guid: string;
  readonly members_mode: MembersMode | null;
  readonly delete_pedigree_count: number;
  readonly delete_pedigree_guids: readonly string[];
  readonly delete_person_count: number;
  readonly delete_patient_count: number;
  readonly delete_individual_count: number;
  readonly delete_family_count: number;
  readonly delete_family_guids: readonly string[];
  readonly unlink_node_count: number;
  readonly unlink_family_guids: readonly string[];
  /** The persons hard-deleted, named for the wizard (guid + display name + is_patient). */
  readonly deleted_persons?: readonly DeletePersonRef[];
  /** The persons detached/unlinked (they survive), named for the wizard. */
  readonly unlinked_persons?: readonly DeletePersonRef[];
  readonly blocked: boolean;
  readonly blocked_reason: string | null;
  readonly requires_confirmation: boolean;
}

/** Spring Batch `BatchStatus` — the core `PhenotipsJobExecutionDTO.status` values (camelCase wire). */
export type DeleteJobStatus =
  | 'STARTING'
  | 'STARTED'
  | 'STOPPING'
  | 'STOPPED'
  | 'COMPLETING'
  | 'COMPLETED'
  | 'FAILED'
  | 'ABANDONED'
  | 'UNKNOWN';

/**
 * `PhenotipsJobExecutionDTO` job handle returned by the DELETE endpoints and by `GET /jobs/{jobId}`.
 * `jobId` is a STRING on the wire; failure detail lives under the nested `exitStatus`.
 */
export interface DeleteJobDto {
  readonly jobId: string;
  readonly jobName: string;
  readonly status: DeleteJobStatus;
  readonly exitStatus?: { readonly exitCode?: string | null; readonly exitDescription?: string | null } | null;
}

/** Terminal Batch states — polling stops and the job's outcome is final once one is reached. */
const TERMINAL_JOB_STATUSES: ReadonlySet<DeleteJobStatus> = new Set<DeleteJobStatus>([
  'COMPLETED',
  'FAILED',
  'STOPPED',
  'ABANDONED',
]);

export function isTerminalDeleteJob(status: DeleteJobStatus): boolean {
  return TERMINAL_JOB_STATUSES.has(status);
}

const POLL_INTERVAL_MS = 2_000;

/** The `/api/v2/pedigree/…` URL for a given suffix, derived from the app's `/api/v1` base. */
function pedigreeV2Url(pathSuffix: string): string {
  const raw = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/core/api/v1';
  const root = raw.replace(/\/+$/, '').replace(/\/api\/v\d+$/, '');
  return `${root}/api/v2/pedigree/${pathSuffix}`;
}

/** The v1 path segment for a person/family target (`PATIENT` → `persons`, `FAMILY` → `families`). */
function v1Segment(targetType: 'PATIENT' | 'FAMILY'): 'persons' | 'families' {
  return targetType === 'PATIENT' ? 'persons' : 'families';
}

/** Only the family branch carries the `members=` query param (f1/f2). */
function membersQuery(
  targetType: DeleteTargetType,
  membersMode: MembersMode | undefined,
): { members: MembersMode } | undefined {
  return targetType === 'FAMILY' && membersMode !== undefined ? { members: membersMode } : undefined;
}

/**
 * `POST /{persons|families}/{guid}/delete-preview` (v1) or `POST /api/v2/pedigree/{guid}/delete-preview`
 * (v2) — the synchronous dry-run. Family previews re-run with `members=UNLINK|DELETE` when the operator
 * flips the f1/f2 choice, so the query key includes `membersMode`.
 */
async function fetchDeletePreview(
  targetType: DeleteTargetType,
  guid: string,
  membersMode: MembersMode | undefined,
  signal: AbortSignal,
): Promise<DeleteImpactDto> {
  if (targetType === 'PEDIGREE') {
    const apiFetch = createPtv2Fetch();
    const response = await apiFetch(pedigreeV2Url(`${encodeURIComponent(guid)}/delete-preview`), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) {
      throw new Error(`Failed to preview delete (HTTP ${String(response.status)})`);
    }
    return (await response.json()) as DeleteImpactDto;
  }
  const query = membersQuery(targetType, membersMode);
  return http.post<DeleteImpactDto>(
    `/${v1Segment(targetType)}/${encodeURIComponent(guid)}/delete-preview`,
    undefined,
    { ...(query !== undefined ? { query } : {}), signal },
  );
}

/** Query factory for a target's delete preview. Disabled until a guid is present. */
export const deletePreviewQuery = (
  targetType: DeleteTargetType,
  guid: string,
  membersMode?: MembersMode,
) =>
  queryOptions({
    queryKey: ['delete-preview', targetType, guid, membersMode ?? null] as const,
    queryFn: ({ signal }) => fetchDeletePreview(targetType, guid, membersMode, signal),
    enabled: guid.length > 0,
    // A preview is a point-in-time dry run; don't serve a stale plan to a destructive confirm.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

/**
 * Execute the delete — `DELETE /{persons|families}/{guid}` (v1) or `DELETE /api/v2/pedigree/{guid}`
 * (v2). Always returns a `PhenotipsJobExecutionDTO` job handle (202); the caller polls it via
 * {@link deleteJobQuery}.
 */
export async function launchDelete(
  targetType: DeleteTargetType,
  guid: string,
  membersMode?: MembersMode,
): Promise<DeleteJobDto> {
  if (targetType === 'PEDIGREE') {
    const apiFetch = createPtv2Fetch();
    const response = await apiFetch(pedigreeV2Url(encodeURIComponent(guid)), {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`Failed to delete pedigree (HTTP ${String(response.status)})`);
    }
    return (await response.json()) as DeleteJobDto;
  }
  const query = membersQuery(targetType, membersMode);
  return http.delete<DeleteJobDto>(
    `/${v1Segment(targetType)}/${encodeURIComponent(guid)}`,
    query !== undefined ? { query } : {},
  );
}

/**
 * Poll a launched delete job. Refetches every 2 s while the Batch status is non-terminal, then stops.
 * Keyed off the job id; disabled until one is present. Mirrors the platform console's `useJob`
 * refetch-interval pattern (there is no shared hook to import).
 */
export const deleteJobQuery = (jobId: string | null) =>
  queryOptions({
    queryKey: ['delete-job', jobId ?? ''] as const,
    queryFn: ({ signal }) => http.get<DeleteJobDto>(`/jobs/${encodeURIComponent(jobId ?? '')}`, { signal }),
    enabled: jobId !== null && jobId.length > 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === undefined) return POLL_INTERVAL_MS;
      return isTerminalDeleteJob(status) ? false : POLL_INTERVAL_MS;
    },
  });
